import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid";
import { useTranslate } from "@refinedev/core";

import { DataGrid } from "@shared/components/DataGrid";
import { formatFinancialError } from "@modules/financial/nfe";
import { financialRowId, toClientsByCategoryWriteParams, toFinancialParams } from "@modules/financial/params";
import { useFinancialListQuery } from "@modules/financial/services";
import { FinancialFilterValues, FinancialRow, FinancialTab } from "@modules/financial/types";

import { CategorySummary } from "./CategorySummary";
import { getFinancialColumns } from "./columns";

const SNAPSHOT_TABS: FinancialTab[] = ["clients_by_plan", "clients_by_category", "media"];

interface FinancialTableProps {
  tab: FinancialTab;
  filters: FinancialFilterValues;
}

export function FinancialTable({ tab, filters }: FinancialTableProps) {
  const translate = useTranslate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
  }, [filtersKey, tab]);

  const needsCpf = tab === "client_plans" && !filters.cpf.trim();
  const isCategoryReport = tab === "clients_by_category" || tab === "plans_by_category";
  const params = useMemo(
    () => toFinancialParams(tab, filters, paginationModel.page + 1, paginationModel.pageSize),
    [tab, filters, paginationModel.page, paginationModel.pageSize]
  );
  const writeParams = useMemo(() => toClientsByCategoryWriteParams(params), [params]);
  const clientsCount =
    tab === "clients_by_category" ? { listParams: params, writeParams } : undefined;

  const columns = getFinancialColumns(tab, translate, undefined, clientsCount);

  const { data, isFetching, isError, error } = useFinancialListQuery<FinancialRow>(
    tab,
    params,
    !needsCpf
  );

  const rows = (data?.results ?? []).map((row, index) => ({
    ...row,
    id: financialRowId(tab, row as unknown as Record<string, unknown>, index),
  }));

  const waitingSnapshot =
    SNAPSHOT_TABS.includes(tab) &&
    !isFetching &&
    !isError &&
    data?.rebuilt_at == null &&
    rows.length === 0;
  const visibleRows = needsCpf ? [] : rows;
  const emptyGrid = visibleRows.length === 0;

  return (
    <Box>
      {(needsCpf || waitingSnapshot || isError || data?.rebuilt_at) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 2, pt: 2 }}>
          {needsCpf && (
            <Alert severity="info">{translate("financial_admin.messages.cpf_required")}</Alert>
          )}
          {waitingSnapshot && (
            <Alert severity="warning">{translate("financial_admin.messages.not_rebuilt")}</Alert>
          )}
          {isError && (
            <Alert severity="error">
              {formatFinancialError(error, translate("financial_admin.messages.load_failed"))}
            </Alert>
          )}
          {data?.rebuilt_at && (
            <Typography variant="caption" color="text.secondary">
              {translate("financial_admin.messages.rebuilt_at")} {new Date(data.rebuilt_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}

      {isCategoryReport ? (
        <CategorySummary
          tab={tab}
          rows={visibleRows}
          loading={isFetching}
          count={needsCpf ? 0 : data?.count ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          clientsCount={clientsCount}
        />
      ) : (
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={isFetching}
          autoHeight={!emptyGrid}
          rowHeight={52}
          columnHeaderHeight={48}
          disableColumnMenu
          rowCount={needsCpf ? 0 : data?.count ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          sx={{
            border: 0,
            minHeight: emptyGrid ? 280 : undefined,
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "grey.50",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
            },
          }}
        />
      )}
    </Box>
  );
}
